/**
 * Performance utilities for React applications
 * Provides comprehensive performance monitoring and optimization tools
 */

import { useRef, useCallback, useMemo } from 'react';

// LRU Cache implementation for memoization
export class LRUCache<K, V> {
  private cache = new Map<K, V>();
  private maxSize: number;

  constructor(maxSize: number = 1000) {
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recently used)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    } else if (this.cache.size >= this.maxSize) {
      // Remove least recently used (first item)
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    this.cache.set(key, value);
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }
}

// Memoization with LRU cache
export function memoizeWithLRU<Args extends readonly unknown[], Return>(
  fn: (...args: Args) => Return,
  options: { maxSize?: number; keySerializer?: (...args: Args) => string } = {}
): (...args: Args) => Return {
  const { maxSize = 1000, keySerializer = JSON.stringify } = options;
  const cache = new LRUCache<string, Return>(maxSize);

  return (...args: Args): Return => {
    const key = keySerializer(...args);
    const cached = cache.get(key);
    
    if (cached !== undefined) {
      return cached;
    }

    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
}

// Debounce utility
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean; maxWait?: number } = {}
): T & { cancel: () => void; flush: () => ReturnType<T> | undefined } {
  const { leading = false, trailing = true, maxWait } = options;
  
  let timeoutId: NodeJS.Timeout | undefined;
  let maxTimeoutId: NodeJS.Timeout | undefined;
  let lastCallTime: number | undefined;
  let lastInvokeTime = 0;
  let lastArgs: Parameters<T>;
  let lastThis: any;
  let result: ReturnType<T>;

  function invokeFunc(time: number) {
    const args = lastArgs;
    const thisArg = lastThis;
    
    lastArgs = undefined as any;
    lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function shouldInvoke(time: number) {
    const timeSinceLastCall = time - (lastCallTime || 0);
    const timeSinceLastInvoke = time - lastInvokeTime;
    
    return (
      lastCallTime === undefined ||
      timeSinceLastCall >= wait ||
      timeSinceLastCall < 0 ||
      (maxWait !== undefined && timeSinceLastInvoke >= maxWait)
    );
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer
    timeoutId = setTimeout(timerExpired, wait - (time - (lastCallTime || 0)));
  }

  function trailingEdge(time: number) {
    timeoutId = undefined;
    
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = undefined as any;
    lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
    if (maxTimeoutId !== undefined) {
      clearTimeout(maxTimeoutId);
    }
    lastInvokeTime = 0;
    lastArgs = undefined as any;
    lastCallTime = undefined;
    lastThis = undefined;
    timeoutId = undefined;
    maxTimeoutId = undefined;
  }

  function flush() {
    return timeoutId === undefined ? result : trailingEdge(Date.now());
  }

  function debounced(this: any, ...args: Parameters<T>) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timeoutId === undefined) {
        // Leading edge
        lastInvokeTime = lastCallTime;
        timeoutId = setTimeout(timerExpired, wait);
        return leading ? invokeFunc(lastCallTime) : result;
      }
      if (maxWait !== undefined) {
        // Handle maxWait
        timeoutId = setTimeout(timerExpired, wait);
        maxTimeoutId = setTimeout(() => invokeFunc(time), maxWait);
        return leading ? invokeFunc(lastCallTime) : result;
      }
    }
    if (timeoutId === undefined) {
      timeoutId = setTimeout(timerExpired, wait);
    }
    return result;
  }

  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced as T & { cancel: () => void; flush: () => ReturnType<T> | undefined };
}

// Throttle utility
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  options: { leading?: boolean; trailing?: boolean } = {}
): T & { cancel: () => void; flush: () => ReturnType<T> | undefined } {
  const { leading = true, trailing = true } = options;
  return debounce(func, wait, { leading, trailing, maxWait: wait });
}

// Performance measurement utilities
export interface PerformanceMetrics {
  renderCount: number;
  renderTimes: number[];
  averageRenderTime: number;
  maxRenderTime: number;
  minRenderTime: number;
  totalRenderTime: number;
  memoryUsage?: number;
  lastMeasurement: number;
}

export class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetrics> = new Map();
  private observers: Set<(key: string, metrics: PerformanceMetrics) => void> = new Set();

  startMeasurement(key: string): () => void {
    const startTime = performance.now();
    const startMemory = (performance as any).memory?.usedJSHeapSize;

    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      const endMemory = (performance as any).memory?.usedJSHeapSize;

      const currentMetrics = this.metrics.get(key) || {
        renderCount: 0,
        renderTimes: [],
        averageRenderTime: 0,
        maxRenderTime: 0,
        minRenderTime: Infinity,
        totalRenderTime: 0,
        lastMeasurement: endTime,
      };

      currentMetrics.renderCount++;
      currentMetrics.renderTimes.push(renderTime);
      
      // Keep only last 100 measurements to prevent memory leaks
      if (currentMetrics.renderTimes.length > 100) {
        currentMetrics.renderTimes.shift();
      }

      currentMetrics.totalRenderTime += renderTime;
      currentMetrics.averageRenderTime = currentMetrics.totalRenderTime / currentMetrics.renderCount;
      currentMetrics.maxRenderTime = Math.max(currentMetrics.maxRenderTime, renderTime);
      currentMetrics.minRenderTime = Math.min(currentMetrics.minRenderTime, renderTime);
      currentMetrics.lastMeasurement = endTime;

      if (startMemory !== undefined && endMemory !== undefined) {
        currentMetrics.memoryUsage = endMemory - startMemory;
      }

      this.metrics.set(key, currentMetrics);
      
      // Notify observers
      this.observers.forEach(observer => observer(key, currentMetrics));
    };
  }

  getMetrics(key: string): PerformanceMetrics | undefined {
    return this.metrics.get(key);
  }

  getAllMetrics(): Record<string, PerformanceMetrics> {
    const result: Record<string, PerformanceMetrics> = {};
    for (const [key, metrics] of this.metrics.entries()) {
      result[key] = { ...metrics };
    }
    return result;
  }

  subscribe(observer: (key: string, metrics: PerformanceMetrics) => void): () => void {
    this.observers.add(observer);
    return () => this.observers.delete(observer);
  }

  clear(key?: string): void {
    if (key) {
      this.metrics.delete(key);
    } else {
      this.metrics.clear();
    }
  }

  // Report performance issues
  getPerformanceIssues(thresholds: {
    maxRenderTime?: number;
    maxAverageRenderTime?: number;
    maxRenderCount?: number;
  } = {}): Array<{ key: string; issue: string; metrics: PerformanceMetrics }> {
    const {
      maxRenderTime = 16, // 60 FPS
      maxAverageRenderTime = 10,
      maxRenderCount = 1000,
    } = thresholds;

    const issues: Array<{ key: string; issue: string; metrics: PerformanceMetrics }> = [];

    for (const [key, metrics] of this.metrics.entries()) {
      if (metrics.maxRenderTime > maxRenderTime) {
        issues.push({
          key,
          issue: `Max render time (${metrics.maxRenderTime.toFixed(2)}ms) exceeds threshold (${maxRenderTime}ms)`,
          metrics,
        });
      }

      if (metrics.averageRenderTime > maxAverageRenderTime) {
        issues.push({
          key,
          issue: `Average render time (${metrics.averageRenderTime.toFixed(2)}ms) exceeds threshold (${maxAverageRenderTime}ms)`,
          metrics,
        });
      }

      if (metrics.renderCount > maxRenderCount) {
        issues.push({
          key,
          issue: `Render count (${metrics.renderCount}) exceeds threshold (${maxRenderCount})`,
          metrics,
        });
      }
    }

    return issues;
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// React hook for performance measurement
export function usePerformanceMeasurement(key: string, enabled: boolean = process.env.NODE_ENV === 'development') {
  const measurementRef = useRef<(() => void) | null>(null);

  const startMeasurement = useCallback(() => {
    if (!enabled) return;
    
    measurementRef.current = performanceMonitor.startMeasurement(key);
  }, [key, enabled]);

  const endMeasurement = useCallback(() => {
    if (!enabled || !measurementRef.current) return;
    
    measurementRef.current();
    measurementRef.current = null;
  }, [enabled]);

  const getMetrics = useCallback(() => {
    return performanceMonitor.getMetrics(key);
  }, [key]);

  return { startMeasurement, endMeasurement, getMetrics };
}

// Batch update utility using React.startTransition
export interface BatchUpdateOptions {
  priority?: 'urgent' | 'normal' | 'low';
  timeout?: number;
}

export class BatchUpdateManager {
  private pendingUpdates: Map<string, () => void> = new Map();
  private timeoutId: NodeJS.Timeout | null = null;
  private isTransitionPending = false;

  scheduleUpdate(key: string, update: () => void, options: BatchUpdateOptions = {}) {
    const { priority = 'normal', timeout = 16 } = options; // 16ms ≈ 1 frame at 60 FPS

    this.pendingUpdates.set(key, update);

    if (priority === 'urgent') {
      this.flushUpdates();
      return;
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.flushUpdates();
    }, timeout);
  }

  private flushUpdates() {
    if (this.pendingUpdates.size === 0) return;

    const updates = Array.from(this.pendingUpdates.values());
    this.pendingUpdates.clear();

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Use startTransition for non-urgent updates
    if (typeof React !== 'undefined' && 'startTransition' in React && !this.isTransitionPending) {
      this.isTransitionPending = true;
      
      (React as any).startTransition(() => {
        updates.forEach(update => update());
        this.isTransitionPending = false;
      });
    } else {
      updates.forEach(update => update());
    }
  }

  cancelUpdates(key?: string) {
    if (key) {
      this.pendingUpdates.delete(key);
    } else {
      this.pendingUpdates.clear();
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  hasPendingUpdates(): boolean {
    return this.pendingUpdates.size > 0;
  }

  getPendingUpdateCount(): number {
    return this.pendingUpdates.size;
  }
}

// React hook for batch updates
export function useBatchUpdates() {
  const batchManagerRef = useRef(new BatchUpdateManager());

  const scheduleUpdate = useCallback((key: string, update: () => void, options?: BatchUpdateOptions) => {
    batchManagerRef.current.scheduleUpdate(key, update, options);
  }, []);

  const cancelUpdates = useCallback((key?: string) => {
    batchManagerRef.current.cancelUpdates(key);
  }, []);

  const hasPendingUpdates = useCallback(() => {
    return batchManagerRef.current.hasPendingUpdates();
  }, []);

  return {
    scheduleUpdate,
    cancelUpdates,
    hasPendingUpdates,
    getPendingUpdateCount: () => batchManagerRef.current.getPendingUpdateCount(),
  };
}

// Memory usage tracking
export function trackMemoryUsage(component: string): () => void {
  const startMemory = (performance as any).memory?.usedJSHeapSize || 0;
  const startTime = Date.now();

  return () => {
    const endMemory = (performance as any).memory?.usedJSHeapSize || 0;
    const endTime = Date.now();
    const memoryDelta = endMemory - startMemory;
    const timeDelta = endTime - startTime;

    if (process.env.NODE_ENV === 'development' && memoryDelta > 1024 * 1024) { // 1MB threshold
      console.warn(`Memory usage spike detected in ${component}: ${(memoryDelta / 1024 / 1024).toFixed(2)}MB over ${timeDelta}ms`);
    }
  };
}

export default {
  LRUCache,
  memoizeWithLRU,
  debounce,
  throttle,
  PerformanceMonitor,
  performanceMonitor,
  usePerformanceMeasurement,
  BatchUpdateManager,
  useBatchUpdates,
  trackMemoryUsage,
};